<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :autoLoad="false"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  edit: [id: Id];
  delete: [id: Id];
}>();

// Get i18n composable
const { t } = useI18n();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/so/service_categories/lista');

const tableColumns = computed(() => {
  return [
    {
      data: 'so_service_category_id',
      title: t('ID'),
      width: 100,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'so_service_category_name',
      title: t('Nome'),
      width: 400,
      sortable: true,
      filterType: 'text'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_service_categories_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data.so_service_category_id;
      return id == null ? '' : `so-service-categories-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

// Methods
const loadData = (): void => {
  const stack = new Error().stack;
  log('SOServiceCategoriesTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('SOServiceCategoriesTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de categorias de serviço'));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOServiceCategoriesTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar categorias de serviço') + ': ' + message);
};

const handleEditClick = (id: Id): void => {
  log('SOServiceCategoriesTable', 'handleEditClick', 'Edit clicked for ID', id);
  emit('edit', id);
};

const handleSaved = (): void => {
  log('SOServiceCategoriesTable', 'event:so-service-categories.saved', 'Received');
  loadData();
};

const handleDeleted = (): void => {
  log('SOServiceCategoriesTable', 'event:so-service-categories.deleted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('SOServiceCategoriesTable', 'mounted', 'Setting up event listeners');
  loadData();
  eventBus.on('so-service-categories.saved', handleSaved);
  eventBus.on('so-service-categories.deleted', handleDeleted);
});

onBeforeUnmount(() => {
  log('SOServiceCategoriesTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('so-service-categories.saved', handleSaved);
  eventBus.off('so-service-categories.deleted', handleDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
